import { useEffect, useId, useMemo, useState } from 'react';
import { History, MoveRight, Package, X } from 'lucide-react';
import { CELL_STATUSES, type CellStatus, type StockPlaceDto, type UpdateCellInput } from '@eombor/contracts';
import { Badge, Button, Input, Skeleton, cn } from '@eombor/ui';
import { Select } from '../../../shared/ui/Select.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { LayoutCell } from '../api/warehouse.api.js';
import { cellStatusLabel, cellStatusTone } from '../model/cell-status.js';
import { CellCode } from './CellCode.js';

export interface CellPanelProps {
  cell: LayoutCell;
  /** Stellaj nomi yoki kodi — katak kodining o'zi qaysi javon ekanini aytmaydi. */
  rackName: string;
  onClose: () => void;
  /** Sig'im — hozircha `GET /warehouses/:id/layout` da yo'q, kartochka uni ixtiyoriy oladi. */
  capacityQty?: number | null;
  capacityWeightKg?: number | null;
  note?: string | null;
  /**
   * Katakdagi partiyalar (`GET /stock/by-cell/:cellId`). Berilmasa bo'lim
   * umuman chizilmaydi — kartochka ma'lumot manbasini o'zi so'ramaydi.
   */
  places?: StockPlaceDto[];
  placesPending?: boolean;
  /** "Tarix" tugmasi — berilmasa tugma chizilmaydi. */
  onShowHistory?: (place: StockPlaceDto) => void;
  /**
   * "Ko'chirish" tugmasi — berilmasa chizilmaydi (ruxsat tekshiruvi
   * chaqiruvchida: `stock.move`).
   *
   * NEGA AYNAN SHU YERDA: mahsulot noto'g'ri katakka qo'yilgani aynan
   * chizmaga qarab turib bilinadi — "bu yerda nima qilyapti?". Tuzatish
   * uchun boshqa ekranga o'tish kerak bo'lsa, odam ko'pincha o'tmaydi:
   * mahsulotni fizik ko'chiradi-yu, tizimda eski katakda qoldirib
   * yuboradi va navbatdagi odam uni topolmaydi.
   */
  onMove?: (place: StockPlaceDto) => void;
  /**
   * Sig'im va holatni saqlash. Berilmasa tahrir bo'limi umuman chizilmaydi —
   * ruxsat tekshiruvi chaqiruvchida (`warehouse.layout.edit`).
   */
  onSave?: (patch: UpdateCellInput) => void;
  savePending?: boolean;
  /**
   * O'rinning kengligi, sm. Ajratish oynasi shu songa tayanadi va
   * kartochkada ham ko'rinib turishi kerak: "30 sm ga bitta mahsulot"
   * degan qaror aynan shu raqamdan boshlanadi.
   */
  widthCm?: number | null;
  /**
   * `dialog` — o'z ramkasi va sarlavhasisiz chiziladi: ularni `Dialog`
   * beradi. Yon paneldagi kartochka ekranni ikkiga bo'lib, chizmani
   * siqib qo'yardi va o'qish uchun ikki joyga qarashga to'g'ri kelardi.
   */
  variant?: 'panel' | 'dialog';
  /**
   * "Bu o'rinda nima turadi" oynasini ochadi. Berilmasa tugma
   * chizilmaydi — ruxsat tekshiruvi chaqiruvchida
   * (`warehouse.layout.edit`).
   *
   * NEGA ALOHIDA OYNA: mahsulotni biriktirish va o'rinni bo'lish —
   * bitta savolning ikki javobi, va ular bir joyda so'ralishi kerak.
   * Kartochkadagi ro'yxat bilan bo'lish oynasidagi ro'yxat yonma-yon
   * turganda esa qaysi biri nima qilishi umuman tushunarsiz edi.
   */
  onAllocate?: () => void;
  className?: string;
}

/**
 * Tahrir qoralamasi: maydonlar MATN bo'lib turadi, son bo'lib emas. Bo'sh
 * maydon "sig'im o'lchanmagan" degani (`null`), nol esa "sig'imi nol" —
 * ikkalasi boshqa ma'no va sonli holatda ular farqlanmay qolardi.
 */
interface CellDraft {
  status: CellStatus;
  capacityQty: string;
  capacityWeightKg: string;
  note: string;
}

const cellDraft = (source: {
  status: CellStatus;
  capacityQty: number | null;
  capacityWeightKg: number | null;
  note: string | null;
}): CellDraft => ({
  status: source.status,
  capacityQty: source.capacityQty === null ? '' : String(source.capacityQty),
  capacityWeightKg: source.capacityWeightKg === null ? '' : String(source.capacityWeightKg),
  note: source.note ?? '',
});

/** Bo'sh matn — `null` ("o'lchanmagan"), aks holda son. */
const numberOrNull = (raw: string): number | null => {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

const Fact = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs text-text-faint">{label}</dt>
    <dd className={cn('text-sm text-text', mono ? 'tabular' : undefined)}>{value}</dd>
  </div>
);

/** Bo'sh nom — server nomni bermagan; ekranda "Nomi noma'lum" turadi. */
const placeName = (place: StockPlaceDto): string | null =>
  place.nomenclatureName.trim() === '' ? null : place.nomenclatureName;

interface CellProduct {
  nomenclatureId: string;
  name: string | null;
  /** Shu mahsulotning ro'yxatdagi BIRINCHI partiyasi — chip shunga olib boradi. */
  batchId: string;
}

/** Katakdagi turli mahsulotlar — partiyalar tartibida, takrorsiz. */
const productsOf = (places: StockPlaceDto[]): CellProduct[] => {
  const seen = new Map<string, CellProduct>();
  for (const place of places) {
    if (!seen.has(place.nomenclatureId)) {
      seen.set(place.nomenclatureId, {
        nomenclatureId: place.nomenclatureId,
        name: placeName(place),
        batchId: place.batchId,
      });
    }
  }
  return [...seen.values()];
};

/** Xulosadagi chiplar soni — qolgani "+N". */
const SUMMARY_CHIPS = 3;

const scrollToBatch = (id: string): void => {
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  document.getElementById(id)?.scrollIntoView?.({
    behavior: reduce ? 'auto' : 'smooth',
    block: 'nearest',
  });
};

/**
 * Partiya kartasi.
 *
 * Birinchi qatorda MAHSULOT NOMI, partiya raqami emas: katakni ochgan odam
 * "P-2026-0079" ni emas, "Автомат выключатель" ni qidiradi. Raqam ikkinchi
 * qatorda — u faqat hujjat bilan solishtirishda kerak.
 */
const BatchCard = ({
  id,
  place,
  onMove,
  onShowHistory,
}: {
  id: string;
  place: StockPlaceDto;
  onMove?: (() => void) | undefined;
  onShowHistory?: (() => void) | undefined;
}) => {
  const name = placeName(place);
  const unit = unitText(place.unit);
  const code = place.nomenclatureCode.trim();
  // Ko'chirish faqat BAND QILINMAGAN qism bo'lsa: band qilingani
  // talabnomaga biriktirilgan va server uni baribir ko'chirmaydi. O'chiq
  // tugma o'rniga tugmaning O'ZI bo'lmagani ma'qul.
  const canMove = onMove !== undefined && place.availableQty > 0;
  const actions = (canMove ? 1 : 0) + (onShowHistory === undefined ? 0 : 1);
  /* 44px — minimal teginish nishoni. `min-h`, `h` emas: `cn` sinflarni
     birlashtirmaydi va ikki `h-*` dan qaysi biri yutishi CSS tartibiga
     bog'liq bo'lib qolardi. */
  const actionClass = 'min-h-[var(--size-control)] w-full sm:w-auto';

  return (
    <li
      id={id}
      className={cn(
        'flex scroll-mt-2 scroll-mb-4 flex-col gap-3 rounded-[var(--radius-md)] border border-border',
        'bg-surface p-3 sm:flex-row sm:items-start sm:justify-between',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          title={name ?? undefined}
          className={cn(
            'line-clamp-2 text-sm leading-5 font-medium break-words',
            name === null ? 'text-text-faint italic' : 'text-text',
          )}
        >
          {name ?? t('warehouse.cellBatchUnnamed')}
        </span>
        {code === '' ? null : (
          <span className="font-mono text-xs break-all text-text-faint">{code}</span>
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted">
          <span className="font-mono text-text">{place.batchNo}</span>
          <span aria-hidden>·</span>
          <span className="tabular">
            {t('warehouse.cellBatchAvailableUnit', { qty: place.availableQty, unit })}
          </span>
          <span aria-hidden>·</span>
          {place.reservedQty > 0 ? (
            <Badge tone="accent" mono>
              {t('warehouse.cellBatchReservedUnit', { qty: place.reservedQty, unit })}
            </Badge>
          ) : (
            <span className="tabular">
              {t('warehouse.cellBatchReservedUnit', { qty: place.reservedQty, unit })}
            </span>
          )}
        </div>
      </div>

      {actions === 0 ? null : (
        <div
          className={cn(
            'grid shrink-0 gap-2 sm:flex sm:items-center',
            actions === 2 ? 'grid-cols-2' : 'grid-cols-1',
          )}
        >
          {canMove ? (
            <Button variant="secondary" size="sm" className={actionClass} onClick={onMove}>
              <MoveRight size={14} aria-hidden />
              {t('warehouse.cellBatchMove')}
            </Button>
          ) : null}
          {onShowHistory === undefined ? null : (
            <Button variant="secondary" size="sm" className={actionClass} onClick={onShowHistory}>
              <History size={14} aria-hidden />
              {t('warehouse.cellBatchHistory')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Katak kartochkasi. Kod har joyda `tabular` (monospace) bilan chiziladi —
 * skladchi uni etiketkadagi bosma bilan belgi-ba-belgi solishtiradi va
 * proporsional shriftda `0`/`O`, `1`/`I` bir-biriga o'xshab ketadi.
 *
 * `Esc` yopadi, lekin fokus qopqoni YO'Q: bu modal emas, yonma-yon panel —
 * `Tab` uni erkin kesib o'tishi kerak.
 */
export const CellPanel = ({
  cell,
  rackName,
  onClose,
  capacityQty = null,
  capacityWeightKg = null,
  note = null,
  places,
  placesPending = false,
  onShowHistory,
  onMove,
  onSave,
  savePending = false,
  widthCm = null,
  variant = 'panel',
  onAllocate,
  className,
}: CellPanelProps) => {
  /* Tahrir qoralamasi. Katak almashsa qoralama ham yangilanadi — `key`
     bilan emas, `cell.id` bo'yicha, chunki panel joyida qoladi. */
  const [draft, setDraft] = useState(() =>
    cellDraft({ status: cell.status, capacityQty, capacityWeightKg, note }),
  );
  useEffect(() => {
    setDraft(cellDraft({ status: cell.status, capacityQty, capacityWeightKg, note }));
  }, [cell.id, cell.status, capacityQty, capacityWeightKg, note]);
  useEffect(() => {
    // Modalda Escape ni `Dialog` ushlaydi — ikkinchi tinglovchi oynani
    // ikki marta yopishga urinardi.
    if (variant === 'dialog') return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, variant]);

  const dash = '—';
  const dialog = variant === 'dialog';
  const batchIdPrefix = useId();
  const batchDomId = (batchId: string): string => `${batchIdPrefix}-batch-${batchId}`;
  const products = useMemo(() => productsOf(places ?? []), [places]);
  const hiddenProducts = products.length - SUMMARY_CHIPS;

  const body = (
    <>
      {/*
        SARLAVHA faqat yon panelda. Modalda uni `Dialog` beradi va shu
        yerda takrorlash ikkita sarlavha bo'lib chiqardi.
      */}
      {dialog ? null : (
        <header className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs text-text-faint">{t('warehouse.cellPanelTitle')}</span>
            <CellCode
              code={cell.code}
              explain
              className="min-w-0 text-lg font-semibold text-text"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('warehouse.cellPanelClose')}
            className={cn(
              '-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center',
              'rounded-[var(--radius-sm)] text-text-faint',
              'transition-[background-color,color] duration-[var(--motion-fast)]',
              'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text',
            )}
          >
            <X size={16} aria-hidden />
          </button>
        </header>
      )}

      {/* Modalda kodning MA'NOSI baribir kerak: sarlavhada faqat kod turadi. */}
      {dialog ? (
        <CellCode code={cell.code} explain className="min-w-0 text-sm text-text-muted" />
      ) : null}

      {/* Stellajning NOMI — kod ostidagi ma'noda yo'q: u yerda "01 stellaj"
          deb turadi, "Yo'l qismlari" esa emas. Kodning takrori bo'lsa
          chizilmaydi. */}
      {rackName === cell.code || rackName.trim() === '' ? null : (
        <p className="text-sm text-text-muted">{rackName}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={cellStatusTone(cell.status)}>{cellStatusLabel(cell.status)}</Badge>
      </div>

      {/* KATAKDA NIMA YOTGANI — xulosada, ro'yxatgacha aylantirmasdan.
          Ko'pi bilan uchta nom, qolgani "+N"; chip shu mahsulotning
          partiyasiga olib boradi. */}
      {placesPending || products.length === 0 ? null : (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-text-faint">{t('warehouse.cellProductsTitle')}</span>
          <ul className="flex flex-wrap gap-1.5">
            {products.slice(0, SUMMARY_CHIPS).map((product) => (
              <li key={product.nomenclatureId} className="min-w-0 max-w-[min(100%,18rem)]">
                <button
                  type="button"
                  title={product.name ?? undefined}
                  onClick={() => scrollToBatch(batchDomId(product.batchId))}
                  className={cn(
                    'flex h-7 max-w-full items-center rounded-[var(--radius-pill)] border border-border',
                    'bg-surface-2 px-2.5 text-xs font-medium text-text',
                    'transition-colors duration-[var(--motion-fast)] hover:border-accent/30 hover:bg-accent-soft',
                    product.name === null ? 'italic text-text-faint' : undefined,
                  )}
                >
                  <span className="truncate">{product.name ?? t('warehouse.cellBatchUnnamed')}</span>
                </button>
              </li>
            ))}
            {hiddenProducts > 0 ? (
              <li
                className="flex h-7 items-center rounded-[var(--radius-pill)] px-2 text-xs text-text-muted tabular"
                title={products
                  .slice(SUMMARY_CHIPS)
                  .map((product) => product.name ?? t('warehouse.cellBatchUnnamed'))
                  .join('\n')}
              >
                {t('warehouse.facadeReservedMore', { count: hiddenProducts })}
              </li>
            ) : null}
          </ul>
        </div>
      )}

      {/*
        BU O'RINDA NIMA TURADI — kartochkaning eng tepasidagi savol.

        Ilgari javob ikki joyga bo'lingan edi: bu yerda belgi, tahrir
        formasida ro'yxat, oynada esa yana bir ro'yxat. Endi bitta joy —
        nima ajratilgani yozib turadi, "O'zgartirish" esa taqsimlash
        oynasini ochadi.
      */}
      <div
        className={cn(
          'border-border flex flex-wrap items-start justify-between gap-2',
          'rounded-[var(--radius-sm)] border p-3',
        )}
      >
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-xs text-text-faint">{t('warehouse.cellFieldReserved')}</span>

          {/* Ro'yxat TO'LIQ chiziladi, "va yana 2 ta" siz: kartochka
              aynan shu savolga javob berish uchun ochiladi va javobning
              yarmini yashirish uni yana bir bosishga majbur qilardi. */}
          {/* "Ajratilmagan" — katak hech bir mahsulotga BIRIKTIRILMAGAN
              (`reservedFor`), bo'sh degani emas. Ichida partiya bo'lsa,
              "bo'sh ekan" deb o'qilmasligi uchun nima yotgani izohlanadi. */}
          {cell.reservedFor.length === 0 ? (
            <>
              <span className="text-text-muted text-sm">{t('warehouse.cellReservedNone')}</span>
              {products.length === 0 ? null : (
                <span className="text-xs text-text-faint">
                  {t('warehouse.cellReservedNoneHint', { count: products.length })}
                </span>
              )}
            </>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {cell.reservedFor.map((item) => (
                <li
                  key={item.id}
                  className={cn(
                    'bg-accent-soft text-accent border-accent/25 flex max-w-full items-center',
                    'gap-1.5 rounded-[var(--radius-pill)] border px-2 py-0.5 text-xs',
                  )}
                >
                  <span className="tabular shrink-0 text-[0.6875rem] opacity-80">{item.code}</span>
                  <span className="truncate font-medium">{item.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {onAllocate === undefined ? null : (
          <Button variant="secondary" size="sm" onClick={onAllocate}>
            <Package size={14} aria-hidden />
            {t('warehouse.allocateAction')}
          </Button>
        )}
      </div>

      {/*
        STELLAJ, QATOR va O'RIN ATAYLAB YO'Q.
        Ular kod ostidagi "A zona · 01 stellaj · 1-qator · 1-o'rin"
        yozuvida allaqachon bor edi va ro'yxatda ikkinchi marta
        takrorlanardi — bir xil narsa ikki xil ko'rinishda, ikkalasini
        ham o'qishga to'g'ri kelardi.
      */}
      <dl className="grid grid-cols-4 gap-4">
        {/* KENGLIK — "30 sm ga bitta mahsulot" degan qaror shu raqamdan
            boshlanadi va uni kartochkadan tashqarida qidirish kerak emas. */}
        <Fact
          label={t('warehouse.cellFieldWidth')}
          value={widthCm === null ? dash : `${widthCm} ${t('builder.unitCm')}`}
          mono
        />
        <Fact label={t('warehouse.cellFieldFill')} value={`${Math.round(cell.fillPercent)}%`} mono />
        <Fact
          label={t('warehouse.cellFieldCapacityQty')}
          value={capacityQty === null ? dash : `${capacityQty} ${t('warehouse.unitPcs')}`}
          mono
        />
        <Fact
          label={t('warehouse.cellFieldCapacityWeight')}
          value={
            capacityWeightKg === null ? dash : `${capacityWeightKg} ${t('warehouse.unitKg')}`
          }
          mono
        />
      </dl>

      {note === null || note === '' ? null : (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-text-faint">{t('warehouse.cellFieldNote')}</span>
          <p className="text-sm text-text-muted">{note}</p>
        </div>
      )}

      {/*
        SIG'IM VA HOLAT — katakning rangi shu yerdan chiqadi.

        Rang qo'lda bo'yalmaydi: u `qoldiq / sig'im` nisbati. Shuning uchun
        bu bo'lim "rangni tanlash" emas, "javon nimaga mo'ljallangani".
        Ikki chekka holat ataylab ochiq qoldirilgan:

          sig'im BO'SH  — o'lchanmagan; ichida narsa bo'lsa katak to'la
                          sanaladi. Yig'ma yuk uchun eng to'g'ri xatti-harakat.
          holat "Band"  — kirim yozilmagan, lekin joy egallangan. Aynan
                          sizning misolingiz: butun katakni egallaydigan
                          bitta narsa qo'yilgan, uni donalab sanashning
                          ma'nosi yo'q.
      */}
      {onSave === undefined ? null : (
        <form
          noValidate
          className="border-border flex flex-col gap-3 border-t pt-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSave({
              status: draft.status,
              capacityQty: numberOrNull(draft.capacityQty),
              capacityWeightKg: numberOrNull(draft.capacityWeightKg),
              note: draft.note.trim() === '' ? null : draft.note.trim(),
            });
          }}
        >
          <h4 className="text-text-muted text-xs font-medium tracking-wide uppercase">
            {t('warehouse.cellEditTitle')}
          </h4>

          <Select
            label={t('warehouse.cellFieldStatus')}
            value={draft.status}
            options={CELL_STATUSES.map((value) => ({ value, label: cellStatusLabel(value) }))}
            onChange={(event) =>
              setDraft((current) => ({ ...current, status: event.target.value as CellStatus }))
            }
          />

          {/* BIRLIK maydonning O'ZIDA turadi. Ilgari "kg" faqat tepadagi
              ko'rsatkichda ko'rinardi va maydonga qarab turgan odam
              "bu kilogrammmi yoki grammmi?" degan savolga javob topa
              olmasdi. Izoh ikkalasiga BIRGA tegishli — ikkovi ham
              ixtiyoriy. */}
          <div className="flex flex-col gap-1">
            <div className="grid grid-cols-2 gap-3">
              <Input
                mono
                type="number"
                min={0}
                label={t('warehouse.cellFieldCapacityQty')}
                suffix={t('warehouse.unitPcs')}
                value={draft.capacityQty}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, capacityQty: event.target.value }))
                }
              />
              <Input
                mono
                type="number"
                min={0}
                label={t('warehouse.cellFieldCapacityWeight')}
                suffix={t('warehouse.unitKg')}
                value={draft.capacityWeightKg}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, capacityWeightKg: event.target.value }))
                }
              />
            </div>
            <p className="text-text-muted text-xs">{t('warehouse.cellCapacityHint')}</p>
          </div>

          <Input
            label={t('warehouse.cellFieldNote')}
            value={draft.note}
            onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))}
          />

          <Button type="submit" size="sm" loading={savePending}>
            {t('warehouse.cellEditSave')}
          </Button>
        </form>
      )}

      {/*
        Partiyalar `stock` kontekstidan keladi (`GET /stock/by-cell/:cellId`).
        Miqdor `tabular`: mavjud va band ustunlari bir-biriga tik chiziladi.
        Mavjud miqdorni SERVER hisoblaydi — bu yerda ayirish yo'q.
      */}
      {places === undefined && !placesPending ? null : (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-text-faint">{t('warehouse.cellBatchesTitle')}</span>

          {placesPending ? (
            /* Karta shaklidagi skelet — yuklangach ro'yxat sakramaydi. */
            <ul aria-busy className="flex flex-col gap-2">
              {[0, 1].map((key) => (
                <li
                  key={key}
                  className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-border p-3"
                >
                  <Skeleton className="w-3/4" />
                  <Skeleton className="w-1/3" />
                  <Skeleton className="w-1/2" />
                </li>
              ))}
            </ul>
          ) : places === undefined || places.length === 0 ? (
            <p className="rounded-[var(--radius-sm)] border border-dashed border-border p-3 text-xs text-text-faint">
              {t('warehouse.cellProductsEmpty')}
            </p>
          ) : (
            /* Tagidagi bo'shliq — oxirgi karta oyna chetiga yopishib qolmasin. */
            <ul className="flex flex-col gap-2 pb-2">
              {places.map((place) => (
                <BatchCard
                  key={place.batchId}
                  id={batchDomId(place.batchId)}
                  place={place}
                  onMove={onMove === undefined ? undefined : () => onMove(place)}
                  onShowHistory={
                    onShowHistory === undefined ? undefined : () => onShowHistory(place)
                  }
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );

  if (dialog) return <div className={cn('flex flex-col gap-4', className)}>{body}</div>;

  return (
    <aside
      aria-label={t('warehouse.cellPanelLabel', { code: cell.code })}
      className={cn(
        'flex flex-col gap-4 rounded-[var(--radius-lg)] border border-border bg-surface p-4',
        className,
      )}
    >
      {body}
    </aside>
  );
};
